import type { Episode, TransitionPlan } from './episode'

export type TherapyPhase = 'insulin_blocked' | 'dka_initial' | 'dka_dextrose' | 'hhs' | 'resolution_pending' | 'resolved' | 'unsupported'
export type FluidGuidanceStatus = 'standard_initial' | 'post_resuscitation' | 'dextrose_phase' | 'individualize' | 'hyperosmolar' | 'resolved'
export type PotassiumGuidanceStatus = 'critical' | 'replace_before_insulin' | 'supplement_fluid' | 'hold_supplement' | 'unknown'

export interface FluidGuidance {
  status: FluidGuidanceStatus
  title: string
  message: string
  referenceRateMlHour?: { low: number; high: number }
  fluidReference?: string
}

export interface PotassiumGuidance {
  status: PotassiumGuidanceStatus
  title: string
  message: string
  replacementRateMmolHour?: number
  fluidSupplementMmolL?: { low: number; high: number }
  targetMmolL?: { low: number; high: number }
}

export interface TherapyGuidance {
  phase: TherapyPhase
  potassiumStatus: 'blocked' | 'ok' | 'unknown'
  potassium: PotassiumGuidance
  fluid: FluidGuidance
  insulinUnitsKgHour?: number
  insulinUnitsHour?: number
  dextrosePhase: boolean
  title: string
  message: string
  transitionState: 'not_ready' | 'candidate' | 'ready'
}

export interface TransitionInput {
  weightKg?: number
  hypoglycemiaRisk: boolean
  homeTddUnits?: number
  stableIvRateUnitsHour?: number
}

export interface TransitionEstimate {
  weightBased?: { low: number; high: number; basis: string }
  homeTddUnits?: number
  ivDerivedTddUnits?: number
  overlapHours: number
  notes: string[]
}

const finite = (value: number | undefined): value is number => typeof value === 'number' && Number.isFinite(value)
const round = (value: number, digits = 1) => {
  const p = 10 ** digits
  return Math.round(value * p) / p
}

const isDka = (diagnosis: Episode['diagnosis']) =>
  diagnosis === 'DKA' || diagnosis === 'EUGLYCEMIC_DKA' || diagnosis === 'MIXED_DKA_HHS'

const hoursBetween = (from: string, to: string) => {
  const hours = (new Date(to).getTime() - new Date(from).getTime()) / 3_600_000
  return Number.isFinite(hours) && hours >= 0 ? hours : undefined
}

function evaluatePotassiumGuidance(potassium: number | undefined): PotassiumGuidance {
  if (!finite(potassium)) {
    return {
      status: 'unknown',
      title: 'Potassio non disponibile',
      message: 'Il riferimento terapeutico sul potassio non viene generato senza una kaliemia corrente.'
    }
  }

  if (potassium <= 2.5) {
    return {
      status: 'critical',
      title: 'Ipokaliemia severa',
      message: 'K ≤2,5 mmol/L è una condizione ad alto rischio. Il consensus raccomanda reintegro di K e rinvio dell’insulina finché K non supera 3,5 mmol/L, con monitoraggio stretto.',
      replacementRateMmolHour: 10,
      targetMmolL: { low: 4, high: 5 }
    }
  }

  if (potassium < 3.5) {
    return {
      status: 'replace_before_insulin',
      title: 'Reintegro K prima dell’insulina standard',
      message: 'Riferimento consensus: iniziare reintegro di K a circa 10 mmol/h e rinviare l’insulina finché K non è >3,5 mmol/L.',
      replacementRateMmolHour: 10,
      targetMmolL: { low: 4, high: 5 }
    }
  }

  if (potassium < 5) {
    return {
      status: 'supplement_fluid',
      title: 'Supplementazione di K nel fluido',
      message: 'Per la maggior parte dei pazienti il consensus indica 20–30 mmol di K per litro di fluido EV, con obiettivo di mantenere K tra 4 e 5 mmol/L.',
      fluidSupplementMmolL: { low: 20, high: 30 },
      targetMmolL: { low: 4, high: 5 }
    }
  }

  return {
    status: 'hold_supplement',
    title: 'Nessun K iniziale nel fluido',
    message: 'Con K ≥5 mmol/L non viene proposta supplementazione iniziale. Rivalutare frequentemente: il K tende a ridursi con fluidi, correzione dell’acidosi e insulina.',
    targetMmolL: { low: 4, high: 5 }
  }
}

function evaluateFluidGuidance(episode: Episode, dextrosePhase: boolean): FluidGuidance {
  const baseline = episode.observations[0].input
  const latest = episode.observations[episode.observations.length - 1]
  const highRisk = Boolean(baseline.heartFailure || baseline.advancedKidneyDisease || baseline.dialysis || baseline.pregnancy || baseline.frailty)
  const elapsed = hoursBetween(episode.startedAt, latest.collectedAt) ?? 0
  const hyperosmolar = episode.diagnosis === 'HHS' || episode.diagnosis === 'MIXED_DKA_HHS' ||
    latest.result.provisionalPattern?.kind === 'HHS_POSSIBLE_NO_KETONES' || latest.result.provisionalPattern?.kind === 'MIXED_POSSIBLE_NO_KETONES'

  if (episode.status === 'resolved' || episode.status === 'closed') {
    return {
      status: 'resolved',
      title: 'Fluidoterapia da rivalutare dopo risoluzione',
      message: 'Dopo la risoluzione della crisi il fabbisogno di fluidi deve essere rivalutato sul bilancio, stato emodinamico, funzione renale e alimentazione.'
    }
  }

  if (highRisk) {
    return {
      status: 'individualize',
      title: 'Fluidoterapia da individualizzare',
      message: 'Rischio di sovraccarico: evitare l’applicazione automatica del ritmo standard. Il consensus suggerisce piccoli boli di cristalloide isotono, ad esempio 250 mL, con rivalutazioni emodinamiche frequenti.',
      fluidReference: 'Cristalloide isotono o bilanciato, boli piccoli con rivalutazione'
    }
  }

  if (dextrosePhase) {
    return {
      status: 'dextrose_phase',
      title: 'Fase con glucosio EV',
      message: 'Nella DKA, quando la glicemia scende <250 mg/dL prima della risoluzione, il fluido deve contenere glucosio 5–10% per consentire la prosecuzione dell’insulina fino alla correzione della chetosi.',
      fluidReference: 'Glucosio 5–10% associato al fluido salino secondo protocollo locale'
    }
  }

  if (elapsed <= 4) {
    return {
      status: hyperosmolar ? 'hyperosmolar' : 'standard_initial',
      title: hyperosmolar ? 'Reidratazione iniziale con guardrail iperosmolari' : 'Reidratazione iniziale',
      message: hyperosmolar
        ? 'Senza compromissione cardio-renale, il riferimento iniziale è 500–1.000 mL/h di soluzione salina isotonica o cristalloide bilanciato nelle prime 2–4 h. In HHS la correzione deve rispettare i limiti di glicemia, sodio e osmolalità.'
        : 'Senza compromissione cardio-renale, il consensus raccomanda soluzione salina isotonica o cristalloide bilanciato a 500–1.000 mL/h nelle prime 2–4 h.',
      referenceRateMlHour: { low: 500, high: 1000 },
      fluidReference: 'NaCl 0,9% oppure cristalloide bilanciato'
    }
  }

  return {
    status: hyperosmolar ? 'hyperosmolar' : 'post_resuscitation',
    title: 'Fluidi dopo la fase iniziale',
    message: hyperosmolar
      ? 'Dopo il ripristino del volume circolante, scelta e velocità dei fluidi dipendono da emodinamica, bilancio, Na e osmolalità. NaCl 0,45% non viene selezionata automaticamente: il consensus la riserva ai casi in cui l’osmolalità non diminuisce nonostante bilancio positivo adeguato e terapia appropriata.'
      : 'Dopo la fase iniziale, scelta e velocità dei fluidi devono essere guidate da pressione, frequenza cardiaca, stato di idratazione, bilancio ingresso/uscita e sodio. Il deficit va corretto generalmente nelle prime 24–48 h.',
    fluidReference: hyperosmolar ? 'Scelta guidata da osmolalità, Na e bilancio' : 'Scelta guidata da emodinamica, bilancio e Na'
  }
}

export function evaluateTherapyGuidance(episode: Episode): TherapyGuidance {
  const latest = episode.observations[episode.observations.length - 1]
  const baseline = episode.observations[0].input
  const potassium = latest.input.potassiumMmolL
  const weight = baseline.weightKg
  const potassiumGuidance = evaluatePotassiumGuidance(potassium)

  const transitionState: TherapyGuidance['transitionState'] = episode.status === 'resolved'
    ? 'ready'
    : episode.status === 'resolution_candidate'
      ? 'candidate'
      : 'not_ready'

  if (episode.status === 'resolved' || episode.status === 'closed') {
    return {
      phase: 'resolved',
      potassiumStatus: finite(potassium) ? (potassium < 3.5 ? 'blocked' : 'ok') : 'unknown',
      potassium: potassiumGuidance,
      fluid: evaluateFluidGuidance(episode, false),
      dextrosePhase: false,
      title: 'Crisi risolta: pianificare la transizione',
      message: 'Usare peso, terapia pre-ricovero e/o fabbisogno EV documentato come stime concorrenti del fabbisogno sottocutaneo.',
      transitionState
    }
  }

  if (episode.status === 'resolution_candidate') {
    return {
      phase: 'resolution_pending',
      potassiumStatus: finite(potassium) ? (potassium < 3.5 ? 'blocked' : 'ok') : 'unknown',
      potassium: potassiumGuidance,
      fluid: evaluateFluidGuidance(episode, false),
      dextrosePhase: false,
      title: 'Criteri compatibili con risoluzione: confermare prima di modificare la fase insulinica',
      message: 'I criteri biochimici di risoluzione sono stati raggiunti. Non aumentare automaticamente l’insulina alla velocità iniziale: confermare clinicamente la risoluzione e pianificare la transizione o un percorso alternativo.',
      transitionState
    }
  }

  if (finite(potassium) && potassium < 3.5) {
    return {
      phase: 'insulin_blocked',
      potassiumStatus: 'blocked',
      potassium: potassiumGuidance,
      fluid: evaluateFluidGuidance(episode, false),
      dextrosePhase: false,
      title: 'Insulina da rinviare per K <3,5 mmol/L',
      message: 'Il percorso standard richiede correzione dell’ipokaliemia prima di iniziare o proseguire l’insulina a velocità standard.',
      transitionState
    }
  }

  const potassiumStatus: TherapyGuidance['potassiumStatus'] = finite(potassium) ? 'ok' : 'unknown'

  if (isDka(episode.diagnosis)) {
    const unresolved = latest.result.resolution.dka !== true
    const dextrosePhase = unresolved && finite(latest.input.glucoseMgDl) && latest.input.glucoseMgDl < 250
    const rateKg = dextrosePhase ? 0.05 : 0.1
    return {
      phase: dextrosePhase ? 'dka_dextrose' : 'dka_initial',
      potassiumStatus,
      potassium: potassiumGuidance,
      fluid: evaluateFluidGuidance(episode, dextrosePhase),
      insulinUnitsKgHour: rateKg,
      insulinUnitsHour: finite(weight) ? round(weight * rateKg, 1) : undefined,
      dextrosePhase,
      title: dextrosePhase ? 'DKA: fase con glucosio EV' : 'DKA / forma mista: riferimento insulinico',
      message: dextrosePhase
        ? 'Con glicemia <250 mg/dL e DKA non risolta, mantenere l’azione insulinica a circa 0,05 U/kg/h e associare glucosio EV secondo protocollo.'
        : 'Riferimento iniziale: infusione EV di insulina a 0,1 U/kg/h, salvo controindicazioni o protocollo locale differente.',
      transitionState
    }
  }

  if (episode.diagnosis === 'HHS') {
    const rateKg = 0.05
    return {
      phase: 'hhs',
      potassiumStatus,
      potassium: potassiumGuidance,
      fluid: evaluateFluidGuidance(episode, false),
      insulinUnitsKgHour: rateKg,
      insulinUnitsHour: finite(weight) ? round(weight * rateKg, 1) : undefined,
      dextrosePhase: false,
      title: 'HHS: riferimento insulinico',
      message: 'In HHS senza acidosi significativa, il riferimento del consensus è 0,05 U/kg/h con particolare attenzione alla velocità di correzione di glicemia e osmolalità.',
      transitionState
    }
  }

  if (latest.result.provisionalPattern) {
    return {
      phase: 'unsupported',
      potassiumStatus,
      potassium: potassiumGuidance,
      fluid: evaluateFluidGuidance(episode, false),
      dextrosePhase: false,
      title: 'Chetoni non disponibili: dose insulinica non automatizzata',
      message: 'Il motore mantiene i guardrail su potassio, glicemia e osmolalità, ma non sceglie automaticamente 0,05 vs 0,1 U/kg/h finché DKA, HHS o forma mista non sono confermate.',
      transitionState
    }
  }

  return {
    phase: 'unsupported',
    potassiumStatus,
    potassium: potassiumGuidance,
    fluid: evaluateFluidGuidance(episode, false),
    dextrosePhase: false,
    title: 'Guida terapeutica non disponibile',
    message: 'Il quadro indice non rientra ancora in DKA, HHS o forma mista classificata.',
    transitionState
  }
}

export function calculateTransitionEstimate(input: TransitionInput): TransitionEstimate {
  const notes: string[] = []
  let weightBased: TransitionEstimate['weightBased']

  if (finite(input.weightKg)) {
    if (input.hypoglycemiaRisk) {
      const dose = round(input.weightKg * 0.3, 1)
      weightBased = { low: dose, high: dose, basis: '0,3 U/kg/die · rischio ipoglicemico' }
    } else {
      weightBased = {
        low: round(input.weightKg * 0.5, 1),
        high: round(input.weightKg * 0.6, 1),
        basis: '0,5–0,6 U/kg/die · insulin-naïve'
      }
    }
  } else {
    notes.push('Peso non disponibile: stima ponderale non calcolabile.')
  }

  const homeTddUnits = finite(input.homeTddUnits) && input.homeTddUnits > 0 ? round(input.homeTddUnits, 1) : undefined
  const ivDerivedTddUnits = finite(input.stableIvRateUnitsHour) && input.stableIvRateUnitsHour > 0
    ? round(input.stableIvRateUnitsHour * 24, 1)
    : undefined

  if (homeTddUnits !== undefined) notes.push('Confrontare la dose domiciliare con HbA1c, aderenza e fattori precipitanti prima di riutilizzarla senza modifiche.')
  if (ivDerivedTddUnits !== undefined) notes.push('La proiezione dal fabbisogno EV può sovrastimare il fabbisogno dopo la risoluzione dello stress metabolico.')
  notes.push('Basal-bolus è il regime preferenziale del consensus; la distribuzione precisa deve seguire protocollo locale, alimentazione e rischio ipoglicemico.')
  notes.push('Nella v0.5 il riferimento operativo per l’overlap è almeno 2 ore tra basale SC e sospensione dell’insulina EV.')

  return {
    weightBased,
    homeTddUnits,
    ivDerivedTddUnits,
    overlapHours: 2,
    notes
  }
}

export function calculatePlannedOverlapHours(plan: Pick<TransitionPlan, 'basalGivenAt' | 'ivStopPlannedAt'>): number | undefined {
  if (!plan.basalGivenAt || !plan.ivStopPlannedAt) return undefined
  const diff = new Date(plan.ivStopPlannedAt).getTime() - new Date(plan.basalGivenAt).getTime()
  if (!Number.isFinite(diff)) return undefined
  return round(diff / 3_600_000, 2)
}

export function transitionPlanIsComplete(plan: TransitionPlan | undefined): boolean {
  if (!plan) return false
  if (plan.status === 'alternative') return Boolean(plan.alternativeReason?.trim())
  if (plan.status !== 'documented') return false
  const overlap = calculatePlannedOverlapHours(plan)
  return Boolean(
    finite(plan.selectedTddUnits) && plan.selectedTddUnits > 0 &&
    finite(plan.basalUnits) && plan.basalUnits > 0 &&
    plan.basalGivenAt && plan.ivStopPlannedAt &&
    overlap !== undefined && overlap >= 2 &&
    plan.feedingStatus && plan.feedingStatus !== 'unknown' &&
    plan.prandialPlan?.trim() && plan.correctionPlan?.trim()
  )
}
